import { Breadcrumb, Flex, Heading, Icon } from '@chakra-ui/react';
import { Fragment, memo, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useBreadcrumbContext } from '@features/navigation/BreadcrumbContext';
import { preventEventDefault } from 'helpers/browserEvent';
import { NavigationEntity, useSidebarNavigationEntities } from 'hooks/useNavigationEntities';
import Info from 'vectors/Info';

interface BreadcrumbItem {
  label: React.ReactNode;
  href: string;
  isCurrent: boolean;
}

const buildBreadcrumbTrail = ({
  pathname,
  flatEntities,
  contextLabel,
}: {
  pathname: string;
  flatEntities: NavigationEntity[];
  contextLabel: string | null;
}): BreadcrumbItem[] => {
  const matchingEntities = flatEntities.filter(
    (e) => e.absolutePath != null && pathname.startsWith(e.absolutePath),
  );

  if (matchingEntities.length === 0) {
    return [];
  }

  matchingEntities.sort((a, b) => (b.absolutePath?.length ?? 0) - (a.absolutePath?.length ?? 0));

  const isOnSubRoute = pathname !== (matchingEntities[0].absolutePath ?? '');
  matchingEntities.reverse();

  const items: BreadcrumbItem[] = matchingEntities.map((entity, index) => ({
    label: entity.navigationTitle ?? entity.name,
    href: entity.absolutePath ?? '',
    isCurrent: !isOnSubRoute && index === matchingEntities.length - 1,
  }));

  if (isOnSubRoute && contextLabel != null) {
    items.push({
      label: contextLabel,
      href: pathname,
      isCurrent: true,
    });
  }

  return items;
};

const NavigationTitle = () => {
  const { selectedNavigationEntity, all } = useSidebarNavigationEntities();
  const [{ label: contextLabel }] = useBreadcrumbContext();
  const pathname = usePathname();

  const breadcrumbItems = useMemo(
    () => buildBreadcrumbTrail({ pathname, flatEntities: all, contextLabel }),
    [pathname, all, contextLabel],
  );

  const showBreadcrumbs = breadcrumbItems.length > 1;

  const href = selectedNavigationEntity?.absolutePath ?? '';
  const isCurrentPath = pathname === href;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isCurrentPath) {
        preventEventDefault(e);
      }
    },
    [isCurrentPath],
  );

  return (
    <Flex align="center" gap={2} minW={0}>
      {showBreadcrumbs ? (
        <Breadcrumb.Root size="md" minW={0}>
          <Breadcrumb.List flexWrap="nowrap" minW={0}>
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              return (
                <Fragment key={item.href}>
                  {/* The last crumb (the page title) absorbs all shrink and
                      truncates; earlier crumbs stay at full width. */}
                  <Breadcrumb.Item minW={0} flexShrink={isLast ? 1 : 0}>
                    {item.isCurrent ? (
                      <Breadcrumb.CurrentLink minW={0}>
                        <Heading size={isLast ? 'sm' : 'md'} truncate={isLast}>
                          {item.label}
                        </Heading>
                      </Breadcrumb.CurrentLink>
                    ) : (
                      <Breadcrumb.Link asChild minW={0}>
                        <Link href={item.href}>
                          <Heading
                            size={isLast ? 'sm' : 'md'}
                            color="text.secondary"
                            truncate={isLast}
                          >
                            {item.label}
                          </Heading>
                        </Link>
                      </Breadcrumb.Link>
                    )}
                  </Breadcrumb.Item>
                  {!isLast && <Breadcrumb.Separator flexShrink={0} />}
                </Fragment>
              );
            })}
          </Breadcrumb.List>
        </Breadcrumb.Root>
      ) : selectedNavigationEntity?.navigationTitle == null ? (
        <Link href={href} onClick={handleClick}>
          <Heading truncate>{selectedNavigationEntity?.name}</Heading>
        </Link>
      ) : (
        <Flex minW={0} overflow="hidden">
          {selectedNavigationEntity?.navigationTitle}
        </Flex>
      )}
      {selectedNavigationEntity?.enableExplanation && (
        <Flex align="center" gap={1} hideBelow="xl" flexShrink={0}>
          <Icon boxSize={4} color="text.secondary">
            <Info />
          </Icon>
          {/* Collapses to just the icon in the xl–2xl band, where the centered
              search bar leaves no room for the label. */}
          <Heading
            fontSize="sm"
            fontWeight="bold"
            color="text.secondary"
            whiteSpace="nowrap"
            hideBelow="2xl"
          >
            How it works
          </Heading>
        </Flex>
      )}
    </Flex>
  );
};

export default memo(NavigationTitle);
